import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { PieChart, Pie, Cell, Label } from "recharts";
import { listUsers, AdminUserItem } from "@/services/adminApi";

// Les comptes B2B (is_business) sont ventilés par tier d'organisation
// (b2b:starter|business|enterprise) plutôt que par leur subscription_type
// backfillé — sinon un membre d'org business apparaît à tort comme "Pro" solo.
const COLORS: Record<string, string> = {
  pro: "hsl(var(--chart-1))",
  admin: "hsl(var(--chart-2))",
  none: "hsl(var(--chart-3))",
  "b2b:starter": "hsl(var(--chart-4))",
  "b2b:business": "hsl(var(--chart-5))",
  "b2b:enterprise": "hsl(38 92% 50%)", // ambre — cohérent avec tierBadge enterprise
};

const chartConfig: ChartConfig = {
  pro: { label: "Pro", color: COLORS.pro },
  admin: { label: "Admin", color: COLORS.admin },
  none: { label: "Sans abo", color: COLORS.none },
  "b2b:starter": { label: "B2B Starter", color: COLORS["b2b:starter"] },
  "b2b:business": { label: "B2B Business", color: COLORS["b2b:business"] },
  "b2b:enterprise": { label: "B2B Enterprise", color: COLORS["b2b:enterprise"] },
};

export function SubscriptionPieCard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await listUsers(navigate);
        setUsers(res.items);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const chartData = useMemo(() => {
    const grouped: Record<string, number> = {};
    for (const u of users) {
      const key =
        u.organization?.is_business && u.organization.tier
          ? `b2b:${u.organization.tier}`
          : u.subscription_type || "none";
      grouped[key] = (grouped[key] || 0) + 1;
    }
    return Object.entries(grouped).map(([name, value]) => ({
      name,
      value,
      fill: COLORS[name] || "hsl(var(--chart-4))",
    }));
  }, [users]);

  const total = users.length;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Repartition abonnements</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Chargement...</div>
        ) : error ? (
          <div className="text-sm text-red-600">Erreur</div>
        ) : (
          <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[250px]">
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={90}
                strokeWidth={2}
              >
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                            {total}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground text-sm">
                            utilisateurs
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
